import type { DevIngestionStatusDto } from '@/api';
import { formatDateTimeDisplay } from '@/utils/date-utils';
import { Table, Tag } from '@lego/connect-components-react';
import { Flex, Text } from '@lego/klik-ui';
import { getIngestionStatusDisplay } from '../dev-dashboard-helpers';
import { CompactTable } from './compact-table';

type Props = {
  status: DevIngestionStatusDto;
};

const formatOptionalDate = (value: Date | null) =>
  value ? formatDateTimeDisplay(value) : '-';

/**
 * Status of the last scheduled ingestion, and the saga rows that are still in the database.
 * Completed ingestions are removed from the database, so only running and failed ingestions show.
 */
export const IngestionStatusPanel = ({ status }: Props) => {
  const display = getIngestionStatusDisplay(status.status);
  const runs = status.runs ?? [];

  return (
    <Flex flexDirection="column" gap={2} flex={1} minHeight={0}>
      <Flex gap={3} alignItems="center" flexWrap="wrap">
        <Tag variant={display.variant} aria-label={display.label}>
          {display.label}
        </Tag>
        <Text>{display.description}</Text>
      </Flex>
      <Text fontSize="sm">
        Last scheduled run: {formatDateTimeDisplay(status.lastScheduledRunAt)}
        {' · '}
        Next scheduled run: {formatDateTimeDisplay(status.nextScheduledRunAt)}
      </Text>
      {runs.length === 0 ? (
        <Text fontSize="sm">No ingestion rows in the database.</Text>
      ) : (
        <CompactTable ariaLabel="Ingestion runs">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.HeaderCell>State</Table.HeaderCell>
              <Table.HeaderCell>Started At</Table.HeaderCell>
              <Table.HeaderCell>Failed At</Table.HeaderCell>
              <Table.HeaderCell>Failed Step</Table.HeaderCell>
              <Table.HeaderCell>Failure</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {runs.map((run) => (
              <Table.Row key={run.correlationId}>
                <Table.Cell>{run.ingestionType ?? '-'}</Table.Cell>
                <Table.Cell>{run.currentState ?? '-'}</Table.Cell>
                <Table.Cell>{formatOptionalDate(run.startedAt)}</Table.Cell>
                <Table.Cell>{formatOptionalDate(run.failedAt)}</Table.Cell>
                <Table.Cell>{run.failedStep ?? '-'}</Table.Cell>
                <Table.Cell>
                  {run.failureType
                    ? `${run.failureType}: ${run.failureReason ?? ''}`
                    : '-'}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </CompactTable>
      )}
    </Flex>
  );
};
